/**
 * Composant Dialog réutilisable pour l'affichage de contenu modal
 * Utilise createPortal pour le rendu dans le DOM
 */

import { useEffect, useId, useRef } from "react";
import { useFenetreModale } from "./use-fenetre-modale";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  closeOnOutsideClick?: boolean;
}

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  className,
  closeOnOutsideClick = true,
}: DialogProps) {
  const idTitre = useId();
  const panneau = useRef<HTMLDivElement>(null);
  const fermer = useRef(onClose);

  useEffect(() => {
    fermer.current = onClose;
  }, [onClose]);

  useFenetreModale(isOpen, panneau, fermer);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-voile/30 backdrop-blur-sm"
      onClick={closeOnOutsideClick ? onClose : undefined}
    >
      <div
        ref={panneau}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? idTitre : undefined}
        tabIndex={-1}
        className={cn(
          "relative max-h-[85vh] w-full max-w-5xl overflow-auto rounded-surface bg-card p-6 shadow-2xl mx-4 outline-none",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 outline-none transition-colors duration-150 hover:bg-charte-hover-strong hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-charte-accent focus-visible:ring-offset-2"
        >
          <X className="h-4 w-4" />
        </button>
        {title && (
          <h2 id={idTitre} className="mb-4 text-xl font-bold text-foreground">
            {title}
          </h2>
        )}
        {/* `p-1` / `-m-1` : le halo de focus des champs (`ring-[3px]`, une ombre
            hors de la boîte) déborde du contenu ; sans cette marge, l'`overflow`
            le rognait sur les côtés. La mise en page ne bouge pas. */}
        <div className="-m-1 overflow-auto p-1">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// Export également sous le nom DocumentModal pour la rétrocompatibilité
export { Dialog as DocumentModal };
